import { pathOr, omit, filter, isNil } from 'ramda';
import { axiosGet } from 'src/api/api';
import { USER_SERVICE_ROOT } from 'src/constants/app.constant';
import { APP_CONSTANT } from 'src/constants';
import { normalizeContactAttributes, getRelationship, transformUserInfoPhoneNumber } from './common.util';

export interface IGuarantor {
  id: string;
  accountId: string;
  nameFirst: string;
  nameMiddle: string;
  nameLast: string;
  address: {
    street: string;
    street2: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
  };
  birthdate: string;
  emailAddress: string;
  phoneNumbers: {
    kind: string;
    number: string;
    extension: string;
  }[];
  status: string;
  relationship: string;
  externalId: string;
  familyAccess: boolean;
  contactAttributes: {
    name: string;
    value?: string;
    externalId: string;
  }[];
}

const fetchCurrentUserInfo = async (authToken, cb) => {
  let userInfoRes = await axiosGet(USER_SERVICE_ROOT, {
    headers: {
      Authorization: `Bearer ${authToken}`
    },
    method: 'GET'
  });

  const userInfoResData = transformUserInfoPhoneNumber(userInfoRes.data);
  userInfoRes = { ...userInfoRes, data: userInfoResData };

  const contactId = pathOr('', ['data', 'id'])(userInfoRes);

  const userAttributeResponse = await axiosGet(
    `${USER_SERVICE_ROOT}/contacts/${contactId}/attributes?namesFilter=ssn,gender,marital_status,communication_type_preference,referral_type,guarantor,primary_insurance,secondary_insurance`,
    {
      headers: {
        Authorization: `Bearer ${authToken}`
      },
      method: 'GET'
    }
  );

  const userAttributes = normalizeContactAttributes(userAttributeResponse.data);

  const guarantors = pathOr([], ['guarantor'], userAttributes);

  const spouse = getRelationship('spouse')(guarantors);
  const guardian_1 = getRelationship('guardian_1')(guarantors);

  const listGuardian = filter(
    (guarantor: IGuarantor) =>
      guarantor.familyAccess && guarantor.relationship === 'Guardian',
    guarantors
  );
  const isMoreThanOneGuardianExists = listGuardian.length > 1;
  let guardian_2;

  if (isMoreThanOneGuardianExists) {
    guardian_2 = getRelationship('guardian_2', { guardian_1 })(guarantors);
  }
  if (isNil(guardian_2)) {
    guardian_2 = getRelationship('guardian_2')(guarantors);
  }

  const billing = getRelationship('billing')(guarantors);

  const primaryInsurance = pathOr({}, ['primary_insurance'], userAttributes);
  const secondaryInsurance = pathOr(
    {},
    ['secondary_insurance'],
    userAttributes
  );

  const userAttributesWithoutGuarantor = omit(
    ['guarantor', 'primary_insurance', 'secondary_insurance'],
    userAttributes
  );

  const user = {
    ...userInfoRes.data,
    ...userAttributesWithoutGuarantor,
    spouse,
    guardian_1,
    guardian_2,
    billing,
    primaryInsurance,
    secondaryInsurance,
    role: userInfoRes.data.isGuardian
      ? APP_CONSTANT.ROLE_LEGAL_GUARDIAN
      : APP_CONSTANT.ROLE_SINGLE_USER
  };

  cb(user);
};

export { fetchCurrentUserInfo };
